import styled from "styled-components";
import {
  space,
  SpaceProps,
  layout,
  LayoutProps,
  typography,
  TypographyProps,
} from "styled-system";

export type StatusIntent =
  | "success"
  | "info"
  | "warning"
  | "danger"
  | "neutral";

/**
 * Unified status badge. Replaces the older StatusDiv / ReviewStatusPill
 * variants with one intent-driven component: a soft tinted background, a
 * strong-coloured label, and an optional leading status dot. Tight geometry
 * for a crisp, modern feel.
 */
interface StatusBadgeProps extends SpaceProps, LayoutProps, TypographyProps {
  intent?: StatusIntent;
  children: React.ReactNode;
  withDot?: boolean;
  /** Uppercase the label (default false — keeps sentence/label casing). */
  uppercase?: boolean;
}

const intentColors = (
  colors: any,
  statusText: any
): Record<StatusIntent, { bg: string; fg: string }> => ({
  success: { bg: colors.green[50], fg: statusText.success },
  info: { bg: colors.blue[50], fg: statusText.info },
  warning: { bg: colors.orange.primary, fg: statusText.warning },
  danger: { bg: colors.red[50], fg: statusText.error },
  neutral: { bg: colors.gray[10], fg: colors.gray[60] },
});

const Badge = styled.span<{ intent: StatusIntent; uppercase?: boolean }>`
  ${({ theme: { colors, radii, fontWeights, statusText }, intent, uppercase }) => {
    const { bg, fg } =
      intentColors(colors, statusText)[intent] ??
      intentColors(colors, statusText).neutral;
    return `
      display: inline-flex;
      align-items: center;
      gap: 6px;
      box-sizing: border-box;
      height: 24px;
      padding: 0 10px;
      border-radius: ${radii[0]};
      background: ${bg};
      color: ${fg};
      font-family: "Inter", sans-serif;
      font-size: 12px;
      font-weight: ${fontWeights.medium};
      line-height: 1;
      white-space: nowrap;
      text-transform: ${uppercase ? "uppercase" : "capitalize"};
      letter-spacing: ${uppercase ? "0.03em" : "normal"};
    `;
  }}
  ${space}
  ${layout}
  ${typography}
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex: 0 0 auto;
`;

export const StatusBadge = (props: StatusBadgeProps) => {
  const { intent = "neutral", children, withDot = true, uppercase, ...styleProps } = props;
  return (
    <Badge intent={intent} uppercase={uppercase} {...styleProps}>
      {withDot && <Dot />}
      {children}
    </Badge>
  );
};

/** Map a raw status string (MandateStatus / IndividualStatus values) to an intent. */
export const statusToIntent = (status?: string): StatusIntent => {
  switch ((status ?? "").toLowerCase()) {
    case "completed":
    case "accepted":
      return "success";
    case "in progress":
    case "new":
      return "info";
    case "in review":
    case "pending":
      return "warning";
    case "rejected":
      return "danger";
    default:
      return "neutral";
  }
};
